import { useEffect, useState } from "react";
import { CancelablePromise } from "../api/psc";
import { createRandomId } from "../utils/random";

export class DynamicListManager<T> {
    results: T[];
    totalCount: number;
    requestInProgress: boolean;
    error?: string;

    filter: any;

    protected reloadHandler: () => void;
    protected loadMoreHandler: () => void;
    protected replaceOrUnshiftHandler: (
        i: T,
        predicate: (i: T) => boolean
    ) => void;
    protected setFilterHandler: (f: any) => void;

    constructor(
        results: T[],
        totalCount: number,
        requestInProgress: boolean,
        reloadHandler: () => void,
        loadMoreHandler: () => void,
        replaceOrUnshiftHandler: (i: T, predicate: (i: T) => boolean) => void,
        error?: string,
        setFilterHandler?: (f: any) => void,
        filter?: any
    ) {
        this.results = results;
        this.totalCount = totalCount;
        this.requestInProgress = requestInProgress;
        this.reloadHandler = reloadHandler;
        this.loadMoreHandler = loadMoreHandler;
        this.replaceOrUnshiftHandler = replaceOrUnshiftHandler;
        this.setFilterHandler = setFilterHandler;
        this.filter = filter;
    }

    get offset() {
        return this.results.length;
    }

    get empty(): boolean {
        return this.results.length === 0 && !this.requestInProgress;
    }

    get hasData(): boolean {
        return this.results.length > 0;
    }

    get hasMore(): boolean {
        return this.totalCount > this.results.length;
    }

    reload() {
        this.reloadHandler();
    }

    loadMore() {
        this.loadMoreHandler();
    }

    setFilter(filter: any) {
        this.setFilterHandler(filter);
    }

    unshiftOrReplace(i: T, predicate: (i: T) => boolean) {
        this.replaceOrUnshiftHandler(i, predicate);
    }
}

/*
 * DynamicListManager<T>: a class with bloew Properties and methods
 * Properties:
 *   results
 *   totalCount
 *   requestInProgress
 *   error
 *   filter
 *
 *   protected reloadHandler
 *   protected loadMoreHandler
 *   protected replaceOrUnshiftHandler
 *   protected setFilterHandler
 *
 * Methods:
 *   offset
 *   empty
 *   hasData
 *   hasMore
 *   reload
 *   loadMore
 *   setFilter
 *   unshiftOrReplace
 */

export default function useDynamicList<T>(
    loader: (offset: number) => any
): DynamicListManager<T> {
    const [requestInProgress, setRequestInProgress] = useState(true);
    const [needsFetch, setNeedsFetch] = useState<boolean | null>(false);
    const [error, setError] = useState<string | null>();
    const [items, setItems] = useState<T[]>([]);
    const [totalCount, setTotalCount] = useState(0);

    const reloadHandler = () => {
        setItems([]);
        setError(null);
        setNeedsFetch(false);
    };

    const unshiftOrReplaceHandler = (i: T, predicate: (i: T) => boolean) => {
        const iToBeReplace = items.find(predicate);
        const updatedItems = [...items];
        if (iToBeReplace) {
            const index = items.indexOf(iToBeReplace);
            if (index !== -1) {
                updatedItems[index] = i;
            }
        } else {
            updatedItems.unshift(i);
        }
        setItems(updatedItems);
    };

    const loadMoreHandler = () => {
        setNeedsFetch(true);
    };

    const manager = new DynamicListManager<T>(
        items,
        totalCount,
        requestInProgress,
        reloadHandler,
        loadMoreHandler,
        unshiftOrReplaceHandler,
        error
    );

    const fetch = async (loadMore: boolean) => {
        setRequestInProgress(true);
        try {
            const result = await loader(manager.offset);

            if (isWithCount(result)) {
                handleResultWithCount(result, loadMore);
            } else {
                handleResult(result, loadMore);
            }
        } catch (e: any) {
            setError(e.getFirstError());
        } finally {
            setRequestInProgress(false);
            setNeedsFetch(null);
        }
    };

    const isWithCount = (
        r: T[] | { results: T[]; totalCount: number }
    ): boolean => {
        return (r as any).count !== undefined;
    };

    const handleResult = (
        r: T[] | { results: T[]; totalCount: number },
        loadMore: boolean
    ): r is T[] => {
        if (loadMore) {
            setItems(items.concat(r as any));
        } else {
            setItems(r as any);
        }
        return true;
    };

    const handleResultWithCount = (
        r: T[] | { results: T[]; totalCount: number },
        loadMore: boolean
    ): r is { results: T[]; totalCount: number } => {
        if (loadMore) {
            setItems(items.concat((r as any).results as any));
        } else {
            setItems((r as any).results);
        }
        setTotalCount((r as any).count ?? 0);
        return true;
    };

    useEffect(() => {
        if (needsFetch === null) {
            return;
        }
        fetch(needsFetch);
    }, [needsFetch]);

    return manager;
}

export function useFilteredDynamicList<T, F>(
    loader: (offset: number, filter: F) => any,
    filterInitialValue?: F
): DynamicListManager<T> {
    const [requestInProgress, setRequestInProgress] = useState(true);
    const [needsFetch, setNeedsFetch] = useState<string | null>(
        createRandomId(5)
    );
    const [error, setError] = useState<string | null>();
    const [items, setItems] = useState<T[]>([]);
    const [totalCount, setTotalCount] = useState(0);
    const [filter, setFilter] = useState<F>(filterInitialValue);
    const [cancellableRequest, setCancellableRequest] =
        useState<CancelablePromise<any> | null>(null);

    const reloadHandler = () => {
        setItems([]);
        setError(null);
        setNeedsFetch(createRandomId(5));
    };

    const setFilterHandler = (f: F) => {
        setItems([]);
        setError(null);
        setFilter(f);
        setNeedsFetch(createRandomId(5));
    };

    const unshiftOrReplaceHandler = (i: T, predicate: (i: T) => boolean) => {
        const iToBeReplace = items.find(predicate);
        const updatedItems = [...items];
        if (iToBeReplace) {
            const index = items.indexOf(iToBeReplace);
            if (index !== -1) {
                updatedItems[index] = i;
            }
        } else {
            updatedItems.unshift(i);
        }
        setItems(updatedItems);
    };

    const loadMoreHandler = () => {
        setNeedsFetch(createRandomId(10));
    };

    const manager = new DynamicListManager<T>(
        items,
        totalCount,
        requestInProgress,
        reloadHandler,
        loadMoreHandler,
        unshiftOrReplaceHandler,
        error,
        setFilterHandler,
        filter
    );

    const fetch = async (loadMore: boolean) => {
        setRequestInProgress(true);
        try {
            if (cancellableRequest) {
                cancellableRequest.cancel();
            }

            const request: Promise<any> = loader(manager.offset, filter);

            const cp = new CancelablePromise<any>(
                async (resolve, reject, onCancel) => {
                    const result = await request;
                    resolve(result);
                }
            );

            setCancellableRequest(cp);

            const result = await cp;

            if (isWithCount(result)) {
                handleResultWithCount(result, loadMore);
            } else {
                handleResult(result, loadMore);
            }
        } catch (e: any) {
            try {
                setError(e.getFirstError());
            } catch {}
        } finally {
            setRequestInProgress(false);
            setNeedsFetch(null);
        }
    };

    const isWithCount = (
        r: T[] | { results: T[]; totalCount: number }
    ): boolean => {
        return (r as any).count !== undefined;
    };

    const handleResult = (
        r: T[] | { results: T[]; totalCount: number },
        loadMore: boolean
    ): r is T[] => {
        if (loadMore) {
            setItems(items.concat(r as any));
        } else {
            setItems(r as any);
        }
        return true;
    };

    const handleResultWithCount = (
        r: T[] | { results: T[]; count: number },
        loadMore: boolean
    ): r is { results: T[]; count: number } => {
        if (loadMore) {
            setItems(items.concat((r as any).results as any));
        } else {
            setItems((r as any).results);
        }
        setTotalCount((r as any).count ?? 0);
        return true;
    };

    useEffect(() => {
        if (needsFetch === null) {
            return;
        }
        fetch(needsFetch.length === 10);
    }, [needsFetch]);

    return manager;
}
